import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { useTranslation } from "react-i18next";
import { MezzaFormField } from "@/components/core/Form/MezzaFormField";
import { MezzaSelectField } from "@/components/core/Form/MezzaSelectField";
import { ChargeFormProps } from "./types";
import {
  getValidationValues,
  validateAmount,
  // validateTip,
  //validateTotalAmount,
} from "./validations";
import { currencies } from "@/common/currencies";
import { ButtonWithLoading } from "@/components/core/Form/ButtonWithLoading";
import { formatNumberWithCommas } from "@/utils/numbers.utils";
import { useActiveLocationsStore } from "@/stores/useActiveLocationsStore";
import { useMemo } from "react";

export function ChargeForm({
  memberId,
  currency,
  onSubmit,
  isLoading = false,
}: ChargeFormProps) {
  const { t } = useTranslation();
  const { availableLocations } = useActiveLocationsStore();

  const upperCaseCurrency = currency.toUpperCase();
  const currencySymbol = currencies[upperCaseCurrency]?.symbol || upperCaseCurrency;
  const errorCurrency = typeof currencySymbol === "string" ? currencySymbol : upperCaseCurrency;

  const locationOptions = useMemo(() => 
    availableLocations.map(loc => ({
      value: loc.locationId,
      label: loc.name,
    })),
    [availableLocations]
  );

  const isSingleLocation = availableLocations.length === 1;

  const validationValues = getValidationValues(upperCaseCurrency);

  const chargeFormSchema = z
    .object({
      locationId: z
        .string({
          required_error: t("charge.validation.locationRequired"),
        })
        .min(1, t("charge.validation.locationRequired")),
      amountToPay: z
        .number({
          required_error: t("charge.validation.amountRequired"),
          invalid_type_error: t("charge.validation.amountRequired"),
        })
        .refine(
          (val) => validateAmount(val, upperCaseCurrency).isValid,
          (val) => ({
            message: t(validateAmount(val, upperCaseCurrency).errorKey || "", {
              currency: errorCurrency,
              minAmount: formatNumberWithCommas(validationValues.amountToPay.min),
              maxAmount: formatNumberWithCommas(validationValues.amountToPay.max),
            }),
          })
        ),
      // tipAmount: z
      //   .number({
      //     required_error: t("charge.validation.tipRequired"),
      //     invalid_type_error: t("charge.validation.tipRequired"),
      //   })
      //   .refine(
      //     (val) => validateTip(val, upperCaseCurrency).isValid,
      //     (val) => ({
      //       message: t(validateTip(val, upperCaseCurrency).errorKey || "", {
      //         currency: currencySymbol,
      //         minTipAmount: formatNumberWithCommas(validationValues.tipAmount.min),
      //       }),
      //     })
      //   ),
      note: z
        .string()
        .max(validationValues.note.max, t("charge.validation.noteMax"))
        .optional(),
    });
    // .refine(
    //   (data) => {
    //     const validation = validateTotalAmount(
    //       data.amountToPay || 0,
    //       data.tipAmount || 0
    //     );
    //     return validation.isValid;
    //   },
    //   {
    //     message: t("charge.validation.totalAmountMax", {
    //       currency: currencySymbol,
    //       maxAmount: formatNumberWithCommas(VALIDATIONS_VALUES.amountToPay.max),
    //     }),
    //     path: ["tipAmount"],
    //   }
    // );

  type ChargeFormValues = z.infer<typeof chargeFormSchema>;

  const form = useForm<ChargeFormValues>({
    resolver: zodResolver(chargeFormSchema),
    defaultValues: {
      locationId: isSingleLocation ? availableLocations[0]?.locationId || "" : "",
      amountToPay: undefined,
      // tipAmount: undefined,
      note: "",
    },
    mode: "all",
  });

  const handleSubmit = async (values: ChargeFormValues) => {
    const amountToPay = values.amountToPay;
    // const tipAmount = values.tipAmount;

    // Validate amount
    const amountValidation = validateAmount(amountToPay, upperCaseCurrency);
    if (!amountValidation.isValid) {
      form.setError("amountToPay", {
        message: t(amountValidation.errorKey!, {
          currency: errorCurrency,
          minAmount: formatNumberWithCommas(validationValues.amountToPay.min),
          maxAmount: formatNumberWithCommas(validationValues.amountToPay.max),
        }),
      });
      return;
    }

    // Validate tip
    // const tipValidation = validateTip(tipAmount);
    // if (!tipValidation.isValid) {
    //   form.setError("tipAmount", {
    //     message: t(tipValidation.errorKey!, {
    //       currency: currencySymbol,
    //       minTipAmount: formatNumberWithCommas(VALIDATIONS_VALUES.tipAmount.min),
    //     }),
    //   });
    //   return;
    // }

    // Validate total amount
    // const totalValidation = validateTotalAmount(amountToPay, tipAmount);
    // if (!totalValidation.isValid) {
    //   form.setError("tipAmount", {
    //     message: t(totalValidation.errorKey!, {
    //       currency: currencySymbol,
    //       maxAmount: formatNumberWithCommas(VALIDATIONS_VALUES.amountToPay.max),
    //     }),
    //   });
    //   return;
    // }

    onSubmit({
      memberId,
      locationId: values.locationId,
      amountToPay: amountToPay,
      tipAmount: 0,
      note: values.note || "",
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="gap-6 overflow-auto px-1 flex-1 flex flex-col">
        <MezzaSelectField
          form={form}
          name="locationId"
          label="locations.location"
          placeholder="charge.selectLocationPlaceholder"
          options={locationOptions}
          disabled={isLoading || isSingleLocation}
        />

        <MezzaFormField
          form={form}
          name="amountToPay"
          label="charge.amountToPay"
          placeholder="charge.amountToPayPlaceholder"
          type="number"
          inputMode="decimal"
          autoFocus={false}
          suffix={currencySymbol}
        />

        {/* <MezzaFormField
          form={form}
          name="tipAmount"
          label="charge.tipAmount"
          placeholder="charge.tipAmountPlaceholder"
          type="number"
          inputMode="decimal"
          prefix={currency}
        /> */}

        <MezzaFormField
          form={form}
          name="note"
          label="charge.note"
          placeholder="charge.notePlaceholder"
          type="textarea"
        />

        <ButtonWithLoading
          type="submit"
          className="w-full !mt-auto"
          disabled={isLoading}
          isLoading={isLoading}
        >
          {t("charge.submit")}
        </ButtonWithLoading>
      </form>
    </Form>
  );
}
